const STOPWORDS = new Set([
  'de',
  'da',
  'do',
  'das',
  'dos',
  'e',
  'com',
  'para',
  'em',
  'a',
  'o',
  'as',
  'os',
  'licenca',
  'ambiental',
  'atividade',
  'atividades',
  'regularizacao',
  'renovacao',
  'operacao',
  'instalacao',
  'previa',
  'ltda',
  'eireli',
  's/a',
]);

export const TIPO_FAMILIES = [
  {
    key: 'lro',
    canonical: 'Licença de Regularização de Operação',
    patterns: [
      /regularizac\w*\s+de\s+licenc\w*\s+de\s+operac/,
      /licenc\w*\s+de\s+regularizac\w*\s+de\s+operac/,
      /regularizac\w*\s+de\s+operac/,
    ],
  },
  {
    key: 'lri',
    canonical: 'Licença de Regularização de Instalação',
    patterns: [
      /regularizac\w*\s+de\s+licenc\w*\s+de\s+instalac/,
      /licenc\w*\s+de\s+regularizac\w*\s+de\s+instalac/,
      /regularizac\w*\s+de\s+instalac/,
    ],
  },
  {
    key: 'rlo',
    canonical: 'Renovação de Licença de Operação',
    patterns: [/renovac\w*\s+(?:de\s+)?licenc\w*\s+de\s+operac/],
  },
  {
    key: 'rli',
    canonical: 'Renovação de Licença de Instalação',
    patterns: [/renovac\w*\s+(?:de\s+)?licenc\w*\s+de\s+instalac/],
  },
  {
    key: 'lp',
    canonical: 'Licença Ambiental Prévia',
    patterns: [/licenc\w*.*previa/, /\bprevia\b/],
  },
  {
    key: 'li',
    canonical: 'Licença Ambiental de Instalação',
    patterns: [/licenc\w*.*instalac/, /autorizac\w*.*implantac/, /autorizac\w*.*instalac/],
  },
  {
    key: 'lo',
    canonical: 'Licença Ambiental de Operação',
    patterns: [/licenc\w*.*operac/, /autorizac\w*.*operac/],
  },
];

/**
 * Sinônimos das atividades oficiais SEMMA (checklists) para casar o
 * nome do empreendimento com um tipo já cadastrado, sem criar outro.
 */
const ACTIVITY_GROUPS = [
  {
    key: 'agricola',
    tokens: ['agricola', 'agricolas', 'irrigacao', 'lavoura', 'cana', 'plantio'],
  },
  {
    key: 'hotelaria',
    tokens: ['hotelaria', 'hotel', 'hoteis', 'pousada', 'resort', 'hospedagem'],
  },
  {
    key: 'obras',
    tokens: [
      'canteiro',
      'canteiros',
      'obra',
      'obras',
      'viario',
      'viarios',
      'concreto',
      'argamassa',
      'cimento',
      'betoneira',
      'construcao',
      'construtora',
    ],
  },
  {
    key: 'comercio',
    tokens: ['comercio', 'servico', 'servicos', 'loja', 'varejo', 'atacado'],
  },
  {
    key: 'habitacional',
    tokens: ['habitacional', 'habitacionais', 'conjunto', 'conjuntos', 'moradia'],
  },
  {
    key: 'imobiliario',
    tokens: ['imobiliario', 'imobiliarios', 'imobiliaria'],
  },
  {
    key: 'parcelamento',
    tokens: ['parcelamento', 'loteamento', 'lote'],
  },
  {
    key: 'saude',
    tokens: ['saude', 'hospital', 'clinica', 'posto', 'ambulatorio'],
  },
  {
    key: 'agua',
    tokens: ['abastecimento', 'agua', 'adutora', 'saneamento', 'eta'],
  },
];

export function normalizeForMatch(value) {
  return String(value || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

export function inferTipoFamily(nome) {
  const n = normalizeForMatch(nome);
  if (!n) return null;
  return TIPO_FAMILIES.find((family) => family.patterns.some((re) => re.test(n))) || null;
}

export function meaningfulTokens(value) {
  return normalizeForMatch(value)
    .split(' ')
    .map((w) => w.replace(/[^a-z0-9]/g, ''))
    .filter((w) => w.length > 3 && !STOPWORDS.has(w));
}

function asHintList(value) {
  if (Array.isArray(value)) return value.map((v) => String(v || '').trim()).filter(Boolean);
  const text = String(value || '').trim();
  return text ? [text] : [];
}

function officialActivity(parsed = {}) {
  return String(parsed.atividade || parsed.atividade_licenciada || '')
    .replace(/\s+/g, ' ')
    .trim();
}

export function activityFromPdf(parsed = {}) {
  return officialActivity(parsed);
}

export function activityHintsFromPdf(parsed = {}) {
  return [officialActivity(parsed), parsed.empreendimento_nome, parsed.atividade_sugerida].filter(
    (v) => String(v || '').trim()
  );
}

export function composeTipoNome(tipoSugerido, atividade) {
  const family = inferTipoFamily(tipoSugerido);
  const fase = family?.canonical || String(tipoSugerido || '').trim();
  const act = asHintList(atividade)[0] || '';
  if (!fase) return act;
  if (!act) return fase;
  const faseN = normalizeForMatch(fase);
  const actN = normalizeForMatch(act);
  if (faseN.includes(actN) || actN.includes(faseN)) return fase;
  return `${fase} de ${act}`;
}

function activityRemainder(nome, family) {
  let n = normalizeForMatch(nome);
  if (family) {
    n = n.replace(normalizeForMatch(family.canonical), ' ');
    for (const re of family.patterns) {
      n = n.replace(re, ' ');
    }
  }
  return n.replace(/\bde\b/g, ' ').replace(/\s+/g, ' ').trim();
}

function tokenOverlap(a, b) {
  if (!a.length || !b.length) return 0;
  const other = new Set(b);
  return a.filter((t) => other.has(t)).length;
}

function groupKeys(tokens) {
  const keys = new Set();
  for (const group of ACTIVITY_GROUPS) {
    if (tokens.some((t) => group.tokens.includes(t))) keys.add(group.key);
  }
  return keys;
}

function expandHintTokens(hints) {
  const tokens = new Set();
  for (const hint of asHintList(hints)) {
    for (const tok of meaningfulTokens(hint)) tokens.add(tok);
  }
  for (const group of ACTIVITY_GROUPS) {
    if ([...tokens].some((t) => group.tokens.includes(t))) {
      group.tokens.forEach((t) => tokens.add(t));
    }
  }
  return [...tokens];
}

/**
 * Encontra tipo existente que combine a fase (LP/LI/LO/LRO…) e a atividade
 * oficial mais próxima do empreendimento. Nunca inventa um tipo novo.
 */
export function findTipoLicencaByTipoEAtividade(tipos, tipoSugerido, atividade) {
  const list = Array.isArray(tipos) ? tipos : [];
  const hints = asHintList(atividade);
  const family = inferTipoFamily(tipoSugerido);
  const composed = family?.canonical || String(tipoSugerido || '').trim();
  const composedN = normalizeForMatch(composed);
  const tipoN = normalizeForMatch(tipoSugerido);
  const actTokens = expandHintTokens(hints);

  const exact = list.find((t) => {
    const tn = normalizeForMatch(t.nome || t.name);
    return tn && tipoN && tn === tipoN;
  });
  if (exact) return { id: exact.id, kind: 'exact', nome: exact.nome || exact.name, composed };
  if (!actTokens.length) {
    const faseExact = list.find((t) => normalizeForMatch(t.nome || t.name) === composedN);
    if (faseExact) {
      return { id: faseExact.id, kind: 'fase', nome: faseExact.nome || faseExact.name, composed };
    }
  }

  let best = null;
  let bestScore = 0;
  let fallbackFase = null;

  for (const t of list) {
    const tn = normalizeForMatch(t.nome || t.name);
    if (!tn) continue;
    const tFamily = inferTipoFamily(tn);
    if (family && tFamily && tFamily.key !== family.key) continue;
    if (family && !tFamily) continue;

    const remainder = activityRemainder(tn, tFamily || family);
    const remTokens = meaningfulTokens(remainder);
    const row = { id: t.id, nome: t.nome || t.name, composed };

    if (!remTokens.length && (tn === tipoN || (family && tn === normalizeForMatch(family.canonical)))) {
      fallbackFase = { ...row, kind: 'fase' };
      if (!actTokens.length) return fallbackFase;
      continue;
    }

    if (!actTokens.length || !remTokens.length) continue;

    const actN = normalizeForMatch(hints.join(' '));
    if (remainder && (remainder === actN || remainder.includes(actN) || actN.includes(remainder))) {
      const score = 200 + remainder.length;
      if (score > bestScore) {
        bestScore = score;
        best = { ...row, kind: 'tipo-atividade' };
      }
      continue;
    }

    const overlap = tokenOverlap(actTokens, remTokens);
    const hintGroups = groupKeys(actTokens);
    const remGroups = groupKeys(remTokens);
    const groupHit = [...hintGroups].some((k) => remGroups.has(k));

    if (overlap > 0) {
      const score = overlap * 20 + (groupHit ? 15 : 0) + remTokens.length;
      if (score > bestScore) {
        bestScore = score;
        best = { ...row, kind: 'tipo-atividade' };
      }
      continue;
    }

    if (groupHit) {
      const score = 12 + remTokens.length;
      if (score > bestScore) {
        bestScore = score;
        best = { ...row, kind: 'tipo-atividade' };
      }
    }
  }

  if (best) return best;
  if (fallbackFase) return fallbackFase;
  return { id: null, kind: 'none', nome: null, composed };
}
